import { useEffect, useMemo, useRef, useState } from 'react';
import { CollapseSection } from '../CollapseSection';
import { PillSectionHeader } from './PillSectionHeader';
import { KText } from '@/ui-library/components';
import { Trash01 } from '@kula-ai/icons-core';
import { handleLinkedInUrlPaste } from '@/utils/linkedinUrlHelpers';
import * as Yup from 'yup';
import { useField } from 'formik';
import { Avatar, Box, Divider, Flex, Link, Text } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { selectUsers } from '@/store/users/selectors';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { getCountryFromTimezone } from '@/utils/countryUtils';
import { useParams } from 'react-router-dom';
import { useFraudDetectionJobEnabled } from '@/components/ats/actions/background-check/fraudDetectorActionHelpers';
import { useCoordinatorJobConfig } from '@/api/ai-coordinator/coordinator-job-config';
import { fetchSelectOptions } from '@/components/formBuilder/helpers/formHelpers';
import { MarkAsReferredField } from '@/components/contacts/candidate-job-drawer/tabs/current-job/candidate-info/sections/MarkAsReferredField';
import { getAtsJobCreate } from '@/store/atsJobCreate';
import { components } from 'react-select';
import { getFirstApplicationReviewStage } from '@/lib/util';
import { generateCustomFormConfig } from '@/formConfigs/generateCustomFormConfig';
import { customFormFields } from '@/lib/customFormFields';
import { useKFlags } from '@/hooks/use-k-flags';
import {
  applyStaticFieldOrder,
  getStaticSection,
} from '@/lib/staticFieldOrder';
import moment from 'moment';
import { CONTACT_SOCIALS } from './constants';
import { UnDeleteCandidateBanner } from '../actions/delete';
import { useCustomFormFields } from '@/api/customFields';
import { TimeZoneSelectorV1 } from '@/components/formBuilder/formFields/DateField/components/TimeZoneSelectorV1';
import {
  REGEX_PATTERNS,
  CREATE_CONTACT_JOB_KEY_REGEX,
} from '@/constants/regex';
import { useGetAllJobsListQuery } from '@/store/query/ats-jobs';
import { JobListMenu } from '../filters/components/UiItems/JobList';
import { ATS_MILESTONES } from '@/ui-library/components/constants';
import { StageAutomationPreview } from '@/components/automation/MoveWithAutomationModal';
import { renderStageOptionLabel } from '@/components/automation/renderStageOptionLabel';
import {
  checkContactMatching,
  getNextHistoryRowId,
  hasValidLinkedInUrl,
} from './CreateContactHelpers';
import { isValidEmail } from '@/constants/regex';
import {
  SelectOptionWithAvatar,
  SelectWithDescriptionOption,
} from '@/components/formBuilder/SelectComponents';
import { getCandidateSourceComponents } from '@/components/formBuilder/SelectComponents/CandidateSourceSelect';

function extractSocials(obj) {
  const kinds = [];

  // Safety check for obj parameter
  if (!obj || typeof obj !== 'object') {
    return kinds;
  }

  // Iterate over all keys in the object
  Object.keys(obj).forEach((key) => {
    const kindMatch = key.match(/^socials_kind_(\d+)$/);

    // Check if the key matches socials_kind_* pattern
    if (kindMatch) {
      const kind = obj[key];
      if (kind) {
        kinds.push(kind);
      }
    }
  });

  return kinds;
}

function extractJobIds(obj) {
  const jobIds = [];

  if (!obj || typeof obj !== 'object') {
    return jobIds;
  }

  Object.keys(obj).forEach((key) => {
    if (CREATE_CONTACT_JOB_KEY_REGEX.test(key) && obj[key]) {
      jobIds.push(obj[key]);
    }
  });

  return jobIds;
}

const collapseSectionProps = () => ({
  headerConfig: {
    containerStyles: () => ({
      backgroundColor: 'ats-tertiary',
      p: '15px',
    }),
    textStyles: {
      color: 'ats-primary',
    },
    iconStyles: {
      width: '24px',
      height: '24px',
    },
  },
  styles: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '6px',
  },
  collapseStyles: {
    display: 'flex',
    flexDirection: 'column',
    // The design puts 16px between fields. `fieldContainerStyles` gives every
    // FormBuilder field `margin: 1em 0`, and flex siblings do not collapse
    // margins, so 16 + 16 read as 32 -- while the one custom-component field
    // (Timezone) carries no margin at all and read as 16. Own the rhythm here
    // instead: zero the children's vertical margins and let one `gap` set it,
    // so every pair is equal whatever component drew the field.
    //
    // Scoped to this drawer deliberately. `fieldContainerStyles` is shared by
    // every form in the app, so changing it there would move spacing on
    // surfaces this design never covered.
    gap: '16px',
    '& > *': { marginTop: 0, marginBottom: 0 },
    // Top 16px: the design's gap between a group heading and its first field.
    // It used to come from the field's own `margin-top`, so this was 0 -- but
    // dropping those margins (see `gap` above) took the gap with them and the
    // first field sat flush against the pill.
    //
    // Bottom 8, not 24: the design asks for 24px between a section's last field
    // and the next pill, and the form grid already contributes 16px between
    // sections. This is the remainder, not the whole gap.
    padding: '16px 2em 8px',
    backgroundColor: 'base-white',
  },
});

export const jobSectionConfig = ({
  section,
  formData,
  formRef,
  setFormData,
  moreOptions = [],
  jobId,
}) => {
  const id = formData[section]?.length + 1;
  const formJobId = `${section}_job_${id}`;
  const formStageId = `${section}_stage_${id}`;
  const customJobFormId = `${section}_customJobFormId_${id}`;
  // Live exclusion of jobs already picked in *other* rows so the same job can't
  // be added twice. Computed lazily (read by JobListMenu on open) rather than
  // frozen into the options list — the field config is created once and cached
  // in state, so a snapshot taken here would go stale the moment another row's
  // selection changes. Reading formRef live keeps it correct. (CORE-4740)
  const getExcludedJobIds = () => {
    const currentRowJobId = formRef?.current?.values?.[formJobId];
    return extractJobIds(formRef?.current?.values || {}).filter(
      (value) => value !== currentRowJobId
    );
  };
  return [
    id > 1 && {
      type: 'custom',
      name: `${section}_divider_${id}`,

      Field: () => {
        return (
          <Box p="10px 0">
            <Divider />
          </Box>
        );
      },
    },
    !jobId && {
      type: 'custom',
      name: `${section}_job_trash_${id}`,
      Field: () => {
        return (
          <Box pos={'relative'}>
            <Trash01
              size="1em"
              className="kula-icon"
              style={{
                marginLeft: '10px',
                position: 'absolute',
                right: '0px',
                cursor: 'pointer',
              }}
              onClick={() => {
                setFormData((prev) => {
                  const updatedSection = prev[section].filter(
                    (item, _index) =>
                      ![
                        formJobId,
                        formStageId,
                        customJobFormId,
                        `${section}_job_trash_${id}`,
                        `${section}_divider_${id}`,
                        `${section}_automation_preview_${id}`,
                      ].includes(item?.name)
                  );

                  return {
                    ...prev,
                    [section]: updatedSection,
                  };
                });
              }}
            />
          </Box>
        );
      },
    },
    {
      type: 'custom',
      name: customJobFormId,
      Field: () => {
        const [_job_field, { value: formJobIdValue }] = useField(formJobId);
        const [, , { setValue }] = useField(formStageId);

        useEffect(() => {
          const fetchJobStageAndUpdateForm = async () => {
            if (!formJobIdValue) {
              // If formJobIdValue is falsy (null, undefined, empty), remove the stage field
              setFormData((prev) => {
                const updatedJobs = prev.jobs.filter(
                  (field) => field.name !== formStageId
                );
                return {
                  ...prev,
                  jobs: updatedJobs,
                };
              });
              return;
            }

            // Fetch job stages by job ID
            const jobStageOptions = {
              endPoint: `/ats_jobs/${formJobIdValue}/ats_job_stages`,
              processResponse: (res) => res?.data,
            };

            const [stagesResponse] = await Promise.all([
              fetchSelectOptions(jobStageOptions),
            ]);
            // Update form data with new options
            setFormData((prev) => {
              const existingStageFieldIndex = prev.jobs.findIndex(
                (i) => i?.name === formStageId
              );
              const newStageField = {
                name: formStageId,
                label: 'Interview Stage',
                isRequired: true,
                type: 'select',
                options: stagesResponse,
                valueKey: 'id',
                labelKey: 'name',
                // Lightning marker on stages that have automation configured.
                customFormatLabel: renderStageOptionLabel,
              };

              const updatedFields = [...prev.jobs];

              if (existingStageFieldIndex > -1) {
                // Update existing stage field
                updatedFields[existingStageFieldIndex] = newStageField;
              } else {
                // Insert new stage field next to the job field
                const jobFieldIndex = prev.jobs.findIndex(
                  (i) => i?.name === formJobId
                );
                updatedFields.splice(jobFieldIndex + 1, 0, newStageField);
              }

              return {
                ...prev,
                jobs: updatedFields,
              };
            });

            // Select the application review as default stage
            const lockedApplicationReviewStage = getFirstApplicationReviewStage(
              stagesResponse,
              ATS_MILESTONES
            );
            // Set value for the stage field
            setValue(lockedApplicationReviewStage?.id);
          };

          fetchJobStageAndUpdateForm();

          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [formJobIdValue]);

        return null;
      },
    },

    {
      name: formJobId,
      label: 'Job',
      type: 'select',
      styleVersion: 'v1',
      useInput: true,
      isRequired: true,
      isMulti: false,
      disabled: jobId,
      defaultValue: moreOptions?.[0]?.value,
      placeholder: `Search jobs`,
      options: moreOptions?.map((item) => ({
        value: item?.value || item?.id,
        label: item?.label || item?.name,
        status: item?.status || 'published',
        department: item?.department,
        locations: item?.locations || [],
      })),
      // Reactive duplicate-job exclusion — JobListMenu calls this on open and
      // hides jobs currently selected in other rows (see getExcludedJobIds).
      getExcludedJobIds,
      newStyles: [
        {
          type: 'menu',
          newStyles: {
            padding: '0px !important',
            borderRadius: '6px !important',
            border: '1px solid var(--kula-dark-secondary-100, #EDEDED)',
            borderColor: 'border-color',
            boxShadow: 'none',
          },
        },
      ],
      components: {
        Menu: JobListMenu,
        IndicatorSeparator: () => null,
      },
      filterOption: () => true,
      validationType: 'array',
    },
    {
      type: 'custom',
      name: `${section}_automation_preview_${id}`,
      // Inline automation preview for the stage picked just above — shows the
      // destination stage's read-only activity/timing summary when that stage
      // has automation configured (renders nothing otherwise). Reads the live
      // stage value so it updates as the user changes the stage.
      Field: () => {
        const [, { value: stageValue }] = useField(formStageId);
        return (
          <StageAutomationPreview
            stageId={stageValue}
            dataTestId={`automation-preview-create-${id}`}
            // The drawer body already scrolls, so grow the preview inline
            // rather than giving the automation info its own inner scrollbar
            // (mirrors the ImportCandidateToJob call site).
          />
        );
      },
    },
  ].filter(Boolean);
};

const flowsSectionConfig = () => {
  return [
    {
      name: `outreach_ids`,
      label: 'Flows',
      placeholder: 'Select or Search for Flows',
      type: 'select',
      fetchConfig: {
        endPoint: `/outreaches/list`,
        processResponse: (res) => res?.data,
        httpType: 'v1',
      },
      isClearable: true,
      valueKey: 'id',
      labelKey: 'title',
    },
  ];
};

const projectsSectionConfig = ({ projectId }) => {
  return [
    {
      name: `project_ids`,
      label: 'Project',
      placeholder: 'Select or Search for projects',
      type: 'select',
      isMulti: true,
      disabled: projectId,
      fetchConfig: {
        endPoint: `/projects/list`,
        processResponse: (res) => res?.data?.projects,
        httpType: 'v1',
      },
      valueKey: 'id',
      labelKey: 'name',
    },
  ];
};

/**
 * The fields inside the Attribution block. `source` and `credited_to` each have
 * their own row, so the caller stamps `isRequired` per field and drops the ones
 * whose row is Hidden. The block's heading is presentation only -- it has no row
 * of its own, and disappears once both fields are gone.
 */
export const attributionsSectionConfig = ({ users }) => {
  return [
    {
      // Use the same hierarchical Source picker as the candidate edit drawer
      // (grouped parents, selectable leaves, selected-state checkmark) instead
      // of a plain grouped select. CandidateSourceMenu builds the tree from the
      // flat list's `parent_id`, so return the raw list (not grouped). CORE-4716.
      name: `ats_candidate_source_id`,
      label: 'Source',
      type: 'kSelect',
      isMulti: false,
      valueKey: 'id',
      labelKey: 'name',
      components: getCandidateSourceComponents(),
      styles: {
        menu: (base) => ({ ...base, padding: 0 }),
        menuList: (base) => ({ ...base, padding: 0 }),
      },
      fetchConfig: {
        endPoint: `ats_candidate_sources`,
        // Only enabled, non-deleted sources — same filter as the edit drawer.
        processResponse: (res) =>
          res?.data?.filter((item) => item?.enabled && !item?.deleted),
      },
    },
    {
      name: `credited_to_user_id`,
      label: 'Credited to',
      customFormatLabel: SelectOptionWithAvatar,
      components: {
        Option: SelectWithDescriptionOption,
      },
      extraProps: {
        showAvatar: true,
        labelKey: 'name',
        descKey: 'email',
        avatarKey: 'avatar',
        isNearLabel: true,
      },
      showAvatar: true,
      type: 'autoComplete',
      options: users,
      fetchConfig: {
        method: 'get',
        endPoint: `account/users/search?filter_by[status][]=active&filter_value=`,
        processResponse: (res) =>
          res?.data?.users?.map((item) => ({
            name: `${item?.first_name} ${item?.last_name}`,
            id: item?.id,
            avatar: item?.avatar_thumb_url,
            ...item,
          })),
        httpType: 'v1',
      },
      updateEndpointOnChange: true,
      loadDefaultOptions: false,
      useInput: true,
      valueKey: 'id',
      labelKey: 'name',
    },
    {
      // Referral toggle whose referrer mirrors the "Credited to" select above —
      // `formik` is injected by FormItem for custom fields. Only shown when
      // Source is "Employee Referral".
      type: 'custom',
      name: 'mark_as_referred',
      Field: MarkAsReferredField,
      props: {
        users,
        sourceFieldName: 'ats_candidate_source_id',
      },
    },
  ];
};

const checkLinkedInMatch = async (
  linkedinUrl,
  formRef,
  setDeleted,
  setDeletedLinkedinValues
) => {
  const formValues = formRef?.current?.values;
  const currentFirstName = formValues?.first_name || '';
  const currentLastName = formValues?.last_name || '';

  if (linkedinUrl) {
    try {
      const method = 'get';
      const path = `contacts/matching?first_name=${currentFirstName}&last_name=${currentLastName}&linkedin=`;
      const endPoint = `${path}${encodeURIComponent(linkedinUrl)}`;

      const response = await fetchSelectOptions({
        method: method,
        endPoint,
      });
      setDeleted({ ...response?.contact });
      const linkedinData = response?.contact || {};
      setDeletedLinkedinValues(linkedinData);
    } catch (error) {
      console.error('Error fetching LinkedIn data:', error);
    }
  }
};

type DeletedLinkedinValues = {
  deleted?: boolean;
  deleted_reason?: string;
  deleted_by?: string;
};

const socialsSectionConfig = ({
  section,
  formData,
  formRef,
  setFormData,
  setDeleted,
}) => {
  const id = formData[section]?.length + 1;
  const formKindId = `${section}_kind_${id}`;
  const formUrlId = `${section}_url_${id}`;
  const trashId = `${section}_trash_${id}`;
  const divId = `${section}_divider_${id}`;
  const kinds = extractSocials(formRef?.current?.values || {});
  let deletedLinkedinValues: DeletedLinkedinValues = {};

  const socialOptions = CONTACT_SOCIALS?.filter(
    (item) => !kinds.includes(item.value)
  );

  // Common function for LinkedIn matching API call

  return [
    id > 1 && {
      type: 'custom',
      name: divId,
      Field: () => {
        return (
          <Box p="10px 0">
            <Divider />
          </Box>
        );
      },
    },
    {
      type: 'custom',
      name: trashId,
      Field: () => {
        return (
          <Box pos={'relative'}>
            <Trash01
              size="1em"
              className="kula-icon"
              style={{
                marginLeft: '10px',
                position: 'absolute',
                right: '0px',
                cursor: 'pointer',
              }}
              onClick={() => {
                setFormData((prev) => {
                  const updatedSection = prev[section].filter(
                    (item) =>
                      ![formKindId, formUrlId, trashId, divId].includes(
                        item?.name
                      )
                  );

                  return {
                    ...prev,
                    [section]: updatedSection,
                  };
                });
              }}
            />
          </Box>
        );
      },
    },
    {
      name: formKindId,
      label: 'Type',
      type: 'select',
      isRequired: true,
      options: socialOptions,
      customOnChange: async (selectedType) => {
        if (selectedType?.value === 'linkedin') {
          const formValues = formRef?.current?.values;
          const urlFieldName = formKindId.replace('_kind_', '_url_');
          const currentUrl = formValues?.[urlFieldName];
          if (currentUrl && REGEX_PATTERNS.linkedin.test(currentUrl)) {
            await checkLinkedInMatch(
              currentUrl,
              formRef,
              setDeleted,
              (data) => {
                deletedLinkedinValues = data;
              }
            );
          }
        } else {
          deletedLinkedinValues = {};
        }
      },
    },
    {
      name: formUrlId,
      label: 'URL',
      isRequired: true,
      type: 'input',
      maxLength: 255,
      onPaste: (e, setValue) => {
        const kindFieldName = formUrlId.replace('_url_', '_kind_');
        const selectedType = formRef?.current?.values?.[kindFieldName];
        if (selectedType === 'linkedin') handleLinkedInUrlPaste(e, setValue);
      },
      debounceOnType: async (value) => {
        const formValues = formRef?.current?.values;
        const kindFieldName = formUrlId.replace('_url_', '_kind_');
        const selectedType = formValues?.[kindFieldName];
        if (
          selectedType === 'linkedin' &&
          value &&
          REGEX_PATTERNS.linkedin.test(value)
        ) {
          await checkLinkedInMatch(value, formRef, setDeleted, (data) => {
            deletedLinkedinValues = data;
          });
        }
      },
      validation: Yup.string()
        .required('URL is required')
        .test('url-validation', function (value) {
          const url = value?.trim();
          if (!url) return true;

          const formValues = this.parent;
          const kindFieldName = formUrlId.replace('_url_', '_kind_');
          const selectedType = formValues?.[kindFieldName];
          const pattern = REGEX_PATTERNS[selectedType];

          const label = selectedType
            ? selectedType.charAt(0).toUpperCase() + selectedType.slice(1)
            : (formUrlId?.split('_url_')?.[0]?.replace(/_/g, ' ') ??
              'social media');

          if (pattern) {
            if (!pattern.test(url)) {
              return this.createError({
                message: `Please enter a valid ${label} URL`,
              });
            }
            return true;
          }

          if (!REGEX_PATTERNS.genericUrl.test(url)) {
            return this.createError({
              message: `Please enter a valid ${label} URL`,
            });
          }

          return true;
        }),
    },
    {
      type: 'custom',
      name: 'delete_banner_for_contact',
      Field: () => {
        if (
          !deletedLinkedinValues ||
          Object.keys(deletedLinkedinValues).length === 0 ||
          !deletedLinkedinValues?.deleted
        )
          return null;
        return (
          <Box mb="12px">
            <UnDeleteCandidateBanner
              reason={deletedLinkedinValues?.deleted_reason}
              deleted_by={deletedLinkedinValues?.deleted_by}
            />
          </Box>
        );
      },
    },
  ].filter(Boolean);
};

const educationSectionConfig = ({
  section,
  formData,
  formRef: _formRef,
  setFormData,
  getDegree,
}) => {
  const id = getNextHistoryRowId(formData[section], section);
  const degreeId = `${section}_degree_${id}`;
  const institution = `${section}_institution_${id}`;
  const startDate = `${section}_startDate_${id}`;
  const endDate = `${section}_endDate_${id}`;
  const endDateDisabled = `${section}_endDateDisabled_${id}`;
  const trashId = `${section}_trash_${id}`;
  const divId = `${section}_divider_${id}`;
  const isPresent = `${section}_isPresent_${id}`;
  const dateValidation = `${section}_dateValidation_${id}`;

  return [
    // `id > 1`: the rule separates one entry from the previous one, so the
    // first entry has nothing to separate from -- it only boxed the fields in
    // against the section header. Jobs and socials already did this.
    id > 1 && {
      type: 'custom',
      name: divId,
      Field: () => {
        return (
          <Box p="10px 0">
            <Divider />
          </Box>
        );
      },
    },
    {
      type: 'custom',
      name: trashId,
      Field: () => {
        return (
          <Box pos={'relative'}>
            <Trash01
              size="1em"
              className="kula-icon"
              style={{
                marginLeft: '10px',
                position: 'absolute',
                right: '0px',
                cursor: 'pointer',
              }}
              onClick={() => {
                setFormData((prev) => {
                  const updatedSection = prev[section].filter(
                    (item) =>
                      ![
                        degreeId,
                        institution,
                        startDate,
                        endDate,
                        endDateDisabled,
                        trashId,
                        divId,
                        isPresent,
                        dateValidation,
                      ].includes(item?.name)
                  );

                  return {
                    ...prev,
                    [section]: updatedSection,
                  };
                });
              }}
            />
          </Box>
        );
      },
    },
    {
      name: institution,
      label: 'School',
      type: 'asyncSelectWithCreate',
      useInput: true,
      placeholder: 'Add school here',
      options: [],
      isRequired: false,
      fetchConfig: {
        endPoint: `/academics/institutions?q=`,
        processResponse: (res) =>
          res?.data?.map((opt) => ({
            ...opt,
            label: opt.name,
            value: opt.id,
          })),
      },
      isMulti: false,
      createPlaceHolder: 'Create as new school',
    },
    {
      name: degreeId,
      label: 'Degree',
      type: 'select',
      isRequired: false,
      placeholder: 'Add degree here',
      labelKey: 'name',
      options: getDegree,
      valueKey: 'id',
      isMulti: false,
    },
    {
      name: startDate,
      label: 'Start Date',
      type: 'monthYear',
    },
    {
      name: endDate,
      label: 'End Date',
      type: 'monthYear',
      isConditional: true,
      conditionConfig: {
        field: isPresent,
        type: 'custom',
        customFunc: (value) => {
          return value !== true;
        },
      },
      fieldValidate: ({ formik }) => {
        const { values } = formik || {};

        const startDateValue = moment(values?.[startDate]);
        const endDateValue = moment(values?.[endDate]);

        if (
          startDateValue?.isValid?.() &&
          endDateValue?.isValid?.() &&
          endDateValue?.isBefore?.(startDateValue)
        ) {
          return 'Invalid education. End date must be after start date.';
        }
      },
    },
    {
      name: dateValidation,
      type: 'custom',
      Field: () => {
        const [, { value: startDateValue }, startHelpers] = useField(startDate);
        const { setError: setStartError } = startHelpers;
        const [, { value: endDateValue }, endHelpers] = useField(endDate);
        const { setError: setEndError } = endHelpers;

        useEffect(() => {
          const validateDates = () => {
            let startDate, endDate;

            // Clear previous errors
            setStartError(undefined);
            setEndError(undefined);

            // Validate start date if present
            if (startDateValue) {
              startDate = moment(startDateValue);
              if (!startDate.isValid()) {
                setStartError('Invalid start date format');
                return;
              }
            }

            // Validate end date if present
            if (endDateValue) {
              endDate = moment(endDateValue);
              if (!endDate.isValid()) {
                setEndError('Invalid end date format');
                return;
              }
            }

            // If both dates are present, ensure end date is after start date
            if (startDate && endDate && endDate.isSameOrBefore(startDate)) {
              setEndError(
                'Invalid education. End date must be after start date.'
              );
            }
          };

          validateDates();
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [startDateValue, endDateValue]);

        return null;
      },
    },
    {
      name: endDateDisabled,
      label: 'End Date',
      type: 'input',
      isRequired: false,
      disabled: true,
      isConditional: true,
      conditionConfig: { field: isPresent, value: true },
      style: {
        opacity: '1 !important',
        background: 'var(--kula-dark-secondary-25, #F9F9FB)',
      },
    },
    {
      name: isPresent,
      label: 'Present',
      type: 'checkbox',
    },
  ].filter(Boolean);
};

const experienceSectionConfig = ({ section, formData, setFormData }) => {
  const id = getNextHistoryRowId(formData[section], section);
  const title = `${section}_title_${id}`;
  const company = `${section}_company_${id}`;
  const startDate = `${section}_startDate_${id}`;
  const endDate = `${section}_endDate_${id}`;
  const endDateDisabled = `${section}_endDateDisabled_${id}`;
  const trashId = `${section}_trash_${id}`;
  const divId = `${section}_divider_${id}`;
  const isPresent = `${section}_isPresent_${id}`;
  const dateValidation = `${section}_dateValidation_${id}`;

  return [
    // `id > 1`: the rule separates one entry from the previous one, so the
    // first entry has nothing to separate from -- it only boxed the fields in
    // against the section header. Jobs and socials already did this.
    id > 1 && {
      type: 'custom',
      name: divId,
      Field: () => {
        return (
          <Box p="10px 0">
            <Divider />
          </Box>
        );
      },
    },
    {
      type: 'custom',
      name: trashId,
      Field: () => {
        return (
          <Box pos={'relative'}>
            <Trash01
              size="1em"
              className="kula-icon"
              style={{
                marginLeft: '10px',
                position: 'absolute',
                right: '0px',
                cursor: 'pointer',
              }}
              onClick={() => {
                setFormData((prev) => {
                  const updatedSection = prev[section].filter(
                    (item) =>
                      ![
                        title,
                        company,
                        startDate,
                        endDate,
                        endDateDisabled,
                        trashId,
                        divId,
                        isPresent,
                        dateValidation,
                      ].includes(item?.name)
                  );

                  return {
                    ...prev,
                    [section]: updatedSection,
                  };
                });
              }}
            />
          </Box>
        );
      },
    },
    {
      name: title,
      label: 'Title',
      isRequired: false,
      type: 'input',
      maxLength: 255,
    },
    {
      name: company,
      label: 'Company',
      type: 'asyncSelectWithCreate',
      useInput: true,
      placeholder: 'Add company here',
      options: [],
      isRequired: false,
      components: {
        Option: ({ children, data, ...props }) => {
          return (
            <components.Option {...props}>
              <Flex alignItems="center">
                <Avatar
                  name={data.name}
                  size="sm"
                  src={`http://${data.logo}`}
                />
                <Box
                  as="span"
                  display="inline-block"
                  ml="sm"
                  textTransform="capitalize"
                >
                  {children}
                </Box>
              </Flex>
            </components.Option>
          );
        },
      },
      fetchConfig: {
        endPoint: `/search/circles/autocomplete?filter_name=company&filter_value=`,
        processResponse: (res) =>
          res?.data?.autocomplete.map((opt) => ({
            ...opt,
            label: opt.name,
            value: opt.id,
          })),
        httpType: 'v1',
      },
      isMulti: false,
      createPlaceHolder: 'Create as new company',
    },
    {
      name: startDate,
      label: 'Start Date',
      type: 'monthYear',
    },
    {
      name: endDate,
      label: 'End Date',
      type: 'monthYear',
      isConditional: true,
      conditionConfig: {
        field: isPresent,
        type: 'custom',
        customFunc: (value) => {
          return value !== true;
        },
      },
      fieldValidate: ({ formik }) => {
        const { values } = formik || {};

        if (!values?.[startDate] || !values?.[endDate]) {
          return;
        }
        const startDateValue = moment(values?.[startDate]);
        const endDateValue = moment(values?.[endDate]);

        if (
          startDateValue?.isValid?.() &&
          endDateValue?.isValid?.() &&
          endDateValue?.isBefore?.(startDateValue)
        ) {
          return 'Invalid experience. End date must be after start date.';
        }
      },
    },
    {
      name: dateValidation,
      type: 'custom',
      Field: () => {
        const [, { value: startDateValue }, startHelpers] = useField(startDate);
        const { setError: setStartError } = startHelpers;
        const [, { value: endDateValue }, endHelpers] = useField(endDate);
        const { setError: setEndError } = endHelpers;

        useEffect(() => {
          const validateDates = () => {
            let startDate, endDate;

            // Clear previous errors
            setStartError(undefined);
            setEndError(undefined);

            // Validate start date if present
            if (startDateValue) {
              startDate = moment(startDateValue);
              if (!startDate.isValid()) {
                setStartError('Invalid start date format');
                return;
              }
            }

            // Validate end date if present
            if (endDateValue) {
              endDate = moment(endDateValue);
              if (!endDate.isValid()) {
                setEndError('Invalid end date format');
                return;
              }
            }

            // If both dates are present, ensure end date is after start date
            if (startDate && endDate && endDate?.isBefore?.(startDate)) {
              setEndError(
                'Invalid experience. End date must be after start date.'
              );
            }
          };

          validateDates();
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [startDateValue, endDateValue]);

        return null;
      },
    },
    {
      name: endDateDisabled,
      label: 'End Date',
      type: 'input',
      disabled: true,
      isConditional: true,
      conditionConfig: { field: isPresent, value: true },
      style: {
        opacity: '1 !important',
        background: 'var(--kula-dark-secondary-25, #F9F9FB)',
      },
    },
    {
      name: isPresent,
      label: 'Present',
      type: 'checkbox',
    },
  ].filter(Boolean);
};

/** settings/candidate-fields section holding the default candidate fields. */
const PERSONAL_INFO_SECTION_ID = 'personal_info';

/**
 * The two rows inside the Attribution block, as static field id -> the formik
 * name each one drives. Each is configured on its own.
 */
const ATTRIBUTION_FIELD_NAMES = [
  { id: 'source', name: 'ats_candidate_source_id' },
  { id: 'credited_to', name: 'credited_to_user_id' },
];

/**
 * The repeatable blocks, as static field id -> the formik/section key each one
 * drives. Their status configures the block as a whole: hidden drops it,
 * mandatory means at least one entry.
 */
/**
 * Settings section id -> the drawer section that renders it. A custom field can
 * be dragged into any of these, and it has to appear where the admin put it
 * rather than falling back to Personal info.
 */
/**
 * The three composite rows -> the drawer section each one renders, as
 * `static field id: formik/section key`. Every one is a whole block: settings
 * decides its position, whether it shows at all, and whether it is Mandatory,
 * but never reaches inside it.
 *
 * Attribution is deliberately NOT here. Source and Credited to render as plain
 * fields inside Personal info rather than as a section of their own, so each is
 * ordered, hidden and stamped exactly like First name is -- see
 * ATTRIBUTION_FIELD_NAMES.
 */
const BLOCK_SECTION_NAMES = {
  socials: 'socials',
  education: 'education',
  experience: 'experience',
};

/** Just the repeatable three — Mandatory on these means "at least one entry". */
const ADDITIONAL_DETAILS_NAMES = {
  socials: 'socials',
  education: 'education',
  experience: 'experience',
};

/**
 * Block wrappers must never be stamped with `isRequired`: a wrapper's own
 * `name` reaches constructYupSchema, and that key holds no formik value, so
 * stamping it makes the drawer permanently unsubmittable.
 */
const BLOCK_ENTRY_NAMES = new Set(Object.values(BLOCK_SECTION_NAMES));

/**
 * The plain fields used to inherit their horizontal padding from the
 * "PERSONAL INFO" CollapseSection that wrapped them. That section is gone --
 * the defaults render as one continuous stream -- so each run of non-block
 * entries gets the padding back here.
 *
 * Only the runs BETWEEN blocks are padded: a block renders its own
 * CollapseSection whose header is deliberately full-bleed, so padding it would
 * inset that header and change how every block looks.
 */
/**
 * The duplicate-email and undelete notices belong under the Email input that
 * raises them, but they cannot be authored there.
 *
 * `applyStaticFieldOrder` moves MANAGED entries between the slots they already
 * occupy and leaves everything else untouched, so an unmanaged notice sitting
 * mid-list splits the managed run and the ordering drifts around it. They are
 * therefore authored last, which is why they used to render after Experience.
 *
 * Moving them once the ordering has finished gets both: the helper sees them
 * trailing, and the form renders them against their input. A no-op when the
 * email entry is absent (the admin can hide it), so they keep their old
 * trailing position rather than disappearing.
 */
const NOTICES_ANCHORED_TO_EMAIL = new Set([
  'duplicate_email_warning',
  'delete_banner_for_contact',
]);

const anchorNoticesToEmail = (entries: any[]) => {
  const notices = entries.filter((entry) =>
    NOTICES_ANCHORED_TO_EMAIL.has(entry?.name)
  );
  if (!notices.length) return entries;

  const rest = entries.filter(
    (entry) => !NOTICES_ANCHORED_TO_EMAIL.has(entry?.name)
  );
  const emailIndex = rest.findIndex((entry) => entry?.name === 'email');
  if (emailIndex === -1) return entries;

  return [
    ...rest.slice(0, emailIndex + 1),
    ...notices,
    ...rest.slice(emailIndex + 1),
  ];
};

const paddedRuns = (entries: any[]) => {
  const out: any[] = [];
  let run: any[] = [];
  let isFirstRun = true;

  const flush = () => {
    if (!run.length) return;
    // The FIRST run keeps the "PERSONAL INFO" collapsible header. A later run
    // (fields the admin ordered after a block) renders as a plain padded group
    // -- it is not "personal info", and repeating the header would read as a
    // second section.
    out.push(
      isFirstRun
        ? {
            type: 'wrapper',
            Wrapper: CollapseSection,
            wrapperProps: {
              title: PERSONAL_INFO_SECTION_TITLE,
              HeaderComp: PillSectionHeader,
              isHeaderClickable: false,
              defaultIsOpen: true,
              ...collapseSectionProps(),
            },
            fields: run,
          }
        : {
            type: 'wrapper',
            // Deliberately NO `name`: a wrapper's name reaches
            // constructYupSchema, and this entry exists only for layout.
            Wrapper: Box,
            wrapperProps: {
              display: 'flex',
              flexDirection: 'column',
              px: '2em',
              // A run can be non-empty in config yet render NOTHING: the
              // duplicate-email and undelete notices return null until they
              // fire, and `mark_as_referred` is gated off in this drawer. The
              // wrapper is still a child of FormBuilder's SimpleGrid, so at
              // 0px tall it sat between two `spacingY` gaps and the 16px
              // between Education and Experience read as 32.
              //
              // `:empty` matches only when React rendered no child at all, so
              // a run that draws something is untouched.
              _empty: { display: 'none' },
            },
            fields: run,
          }
    );
    isFirstRun = false;
    run = [];
  };

  entries.filter(Boolean).forEach((entry) => {
    if (BLOCK_ENTRY_NAMES.has(entry?.name)) {
      flush();
      out.push(entry);
      return;
    }
    run.push(entry);
  });
  flush();
  return out;
};

/** Header for the group holding custom fields left in the Custom fields section. */
const CUSTOM_FIELDS_SECTION_TITLE = 'Custom fields';

/**
 * Header for the group of default fields, per the design. Note this differs
 * from settings/candidate-fields, which titles the same group "Default fields"
 * from the section label the backend serves.
 */
const PERSONAL_INFO_SECTION_TITLE = 'Personal info';

/**
 * Static rows each job agent needs to reach the candidate. Fraud detector
 * calls, so it needs Phone; Coordinator also schedules the call, so it needs
 * Timezone as well.
 */
const FRAUD_REQUIRED_FIELD_IDS = ['phone_number'];
const COORDINATOR_REQUIRED_FIELD_IDS = ['phone_number', 'time_zone'];

/** static field id -> the formik field name it drives in this drawer. */
const CONTACT_FIELD_NAMES = {
  first_name: 'first_name',
  last_name: 'last_name',
  email: 'email',
  phone_number: 'phone_number',
  time_zone: 'time_zone',
  location: 'location',
  tags: 'tags',
  skills: 'skills',
};

export const profileSectionConfig = ({
  Country,
  customFields,
  firstName,
  lastName,
  setDeleted,
  isDeletedContact,
  setDuplicateContact,
  duplicateContact,
  hasLinkedInUrl,
  defaultPhoneCountry,
  staticFields,
  blockEntries = [],
  resumeValues = {},
  isFraudEnabled = false,
  isCoordinatorEnabled = false,
}) => {
  // A custom field can be dragged into Personal info in settings, so the
  // section lists static and custom rows in one order. Resolve those rows to
  // the formik names generateCustomFormConfig gave them and put them ahead of
  // the ones that stayed in Custom fields, so the managed run stays contiguous
  // — applyStaticFieldOrder writes the configured order back into the slots
  // the managed entries already hold.
  const personalInfo = getStaticSection(staticFields, PERSONAL_INFO_SECTION_ID);
  const sectionCustomNames = new Set(
    personalInfo
      .filter((field) => field?.field_kind === 'custom')
      .map((field) => `custom_${field.id}`)
  );
  const fieldNames = {
    ...CONTACT_FIELD_NAMES,
    // The three composite rows resolve to the drawer section each one renders.
    ...BLOCK_SECTION_NAMES,
    // Source and Credited to are plain fields in this stream, so each resolves
    // to its own formik name and is ordered/hidden/stamped on its own row.
    ...Object.fromEntries(
      ATTRIBUTION_FIELD_NAMES.map(({ id, name }) => [id, name])
    ),
    ...Object.fromEntries(
      Array.from(sectionCustomNames).map((name) => [
        name.replace('custom_', ''),
        name,
      ])
    ),
  };
  // A hidden field the RESUME PARSE actually filled. Hiding governs what the
  // form asks for; once parsing has produced a value, dropping the row would
  // discard data we already extracted and the recruiter never sees it. Scoped
  // to the parsed values on purpose — a job/flow default or typed input must
  // not resurrect a hidden row. An empty parsed value stays hidden.
  const hasValue = (value) =>
    Array.isArray(value)
      ? value.length > 0
      : value !== undefined && value !== null && value !== '';
  const revealHiddenIds = new Set(
    personalInfo
      .filter(
        (field) =>
          field?.hidden && hasValue(resumeValues?.[fieldNames[field.id]])
      )
      .map((field) => String(field.id))
  );

  // Inside a job, what its agents need is Mandatory whatever
  // settings/candidate-fields says (CORE-5348): Fraud detector needs Phone,
  // Coordinator needs Phone and Timezone. Same rule the application-form
  // builder renders as an immutable Mandatory with its "required for ..."
  // tooltip. Revealed as well as required: a hidden field cannot be
  // mandatory, the recruiter could never fill it.
  const forceRequiredIds = new Set<string>([
    ...(isFraudEnabled ? FRAUD_REQUIRED_FIELD_IDS : []),
    ...(isCoordinatorEnabled ? COORDINATOR_REQUIRED_FIELD_IDS : []),
  ]);
  forceRequiredIds.forEach((id) => revealHiddenIds.add(id));

  // A custom field the admin hid. applyStaticFieldOrder already drops the ones
  // placed among the defaults, but a custom field left in the Custom fields
  // section is an UNMANAGED trailing entry the helper never sees — so hiding
  // one there looked like it worked in settings and still rendered here.
  const hiddenCustomNames = new Set(
    (staticFields || []).flatMap((section: any) =>
      (section?.fields || [])
        .filter((f: any) => f?.field_kind === 'custom' && f?.hidden)
        .map((f: any) => `custom_${f.id}`)
    )
  );

  // A custom field is either placed in the default section -- where it is a
  // MANAGED entry, ordered among the defaults -- or left in Custom fields,
  // where it renders in its own group after the stream (see below). The two
  // cannot share a run: a managed entry inside the trailing group would be
  // ordered into a slot the group does not own.
  const visibleCustomFields = customFields.filter(
    (entry) => !hiddenCustomNames.has(entry?.name)
  );
  const placedCustomFields = visibleCustomFields.filter((entry) =>
    sectionCustomNames.has(entry?.name)
  );
  const trailingCustomFields = visibleCustomFields.filter(
    (entry) => !sectionCustomNames.has(entry?.name)
  );

  // One ordered stream: the default fields, the three composite blocks, Source
  // and Credited to, and any custom field dragged in among them all order
  // against ONE list, so a field placed after Education renders after it.
  // Warning/banner entries are unmanaged and trail the run untouched. An
  // account with no static_fields keeps the order below.
  const orderedStream = paddedRuns(
    anchorNoticesToEmail(
      applyStaticFieldOrder(
        [
          {
            name: 'first_name',
            label: 'First name',
            type: 'input',
            maxLength: 255,
            isRequired: true,
          },
          {
            name: 'last_name',
            label: 'Last name',
            maxLength: 255,
            type: 'input',
          },
          {
            name: 'email',
            label: 'Email',
            type: 'input',
            inputType: 'email',
            maxLength: 255,
            isRequired: !hasLinkedInUrl,
            optionalValidation: true,
            validation: Yup.string()
              .nullable()
              .test(
                'email-or-linkedin',
                'Email or LinkedIn URL is required',
                function (value) {
                  const formValues = this.parent;

                  // Check if a valid LinkedIn URL exists in the socials section
                  if (hasValidLinkedInUrl(formValues)) {
                    return true;
                  }

                  // If no LinkedIn URL, email is required
                  if (!value || !value.trim()) {
                    return false; // Will show "Email or LinkedIn URL is required"
                  }

                  return true;
                }
              )
              .test(
                'email-format',
                'Valid email address required',
                function (value) {
                  // Only validate format if value is provided
                  if (!value || !value.trim()) {
                    return true; // Let the above test handle empty case
                  }
                  return isValidEmail(value);
                }
              ),

            debounceOnType: async (value) => {
              // Only check once the email is a valid address
              if (!isValidEmail(value)) {
                setDeleted(false);
                setDuplicateContact?.(false);
                return;
              }

              const contact = await checkContactMatching(
                firstName,
                lastName,
                value
              );
              if (contact?.deleted) {
                setDeleted({
                  ...contact,
                  deletedBannerForEmailMatch: true,
                });
                setDuplicateContact?.(false);
              } else if (contact?.id) {
                // An active candidate already uses this email
                setDuplicateContact?.(contact);
                setDeleted(false);
              } else {
                // Clear both states if no matching contact is found
                setDeleted(false);
                setDuplicateContact?.(false);
              }
            },
          },
          {
            name: 'phone_number',
            label: 'Phone',
            type: 'phone',
            placeholder: 'Add phone number',
            defaultCountry: defaultPhoneCountry,
            inputSize: 'md',
            optionalValidation: true,
            countrySelectorTriggerClassName: 'w-[100px]',
            syncTimezone: true,
          },
          {
            type: 'custom',
            name: 'time_zone',
            // FormItem renders a custom field's component and nothing else, so
            // this label never reaches the DOM (TimeZoneSelectorV1 draws its
            // own). It is here for the validation message: settings can mark
            // Timezone mandatory, and without a label the required rule reads
            // "undefined is required".
            label: 'Timezone',
            // The selector draws its own label, so it also has to draw its own
            // required marker -- FormItem has no label of its own to mark.
            // Without this an admin who sets Timezone mandatory gets a field the
            // form rejects on submit with nothing on it saying why.
            Field: ({ formik, isRequired }) => {
              return (
                <TimeZoneSelectorV1
                  value={formik.values.time_zone}
                  onChange={(value) => {
                    formik.setFieldValue('time_zone', value.key);
                  }}
                  inputProps={{
                    size: 'sm-semi-bold',
                    menuPlacement: 'auto',
                    menuPortalTarget: document.body,
                    isRequired,
                    customStyles: {
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    },
                  }}
                />
              );
            },
          },
          {
            name: 'location',
            label: 'Location',
            type: 'autoComplete',
            styleVersion: 'v1',
            menuPlacement: 'auto',
            validationType: 'object',
            labelKey: 'full_address',
            useInput: true,
            initialSearchQuery: Country,
            placeholder: `Search a location`,
            updateEndpointOnChange: true,
            fetchConfig: {
              method: 'get',
              endPoint: `/ats_offices/search_location?q=`,
              processResponse: (res) =>
                res?.data?.map((item) => ({
                  label: item?.full_address,
                  value: item,
                  ...item,
                })),
            },
          },
          {
            name: 'tags',
            label: 'Tags',
            type: 'asyncSelectWithCreate',
            menuPlacement: 'auto',
            useInput: true,
            placeholder: 'Add tags here',
            isMulti: true,
            fetchConfig: {
              endPoint: `/search/circles/autocomplete_tags?filter_name=tags&filter_value=`,
              processResponse: (res) =>
                res?.data?.autocomplete.map((opt) => ({
                  ...opt,
                  label: opt.value,
                  value: opt.value,
                })),
              httpType: 'v1',
            },
            createPlaceHolder: 'Create as new tag and add',
            containerStyles: {},
          },
          {
            name: 'skills',
            label: 'Skills',
            type: 'asyncSelectWithCreate',
            useInput: true,
            placeholder: 'Add skills',
            isMulti: true,
            fetchConfig: {
              endPoint: `/search/circles/autocomplete_skills?filter_name=skills&include_global=true&filter_value=`,
              processResponse: (res) =>
                res?.data?.autocomplete.map((opt) => ({
                  ...opt,
                  label: opt.value,
                  value: opt.value,
                })),
              httpType: 'v1',
            },
            createPlaceHolder: 'Create as new skill and add',
            containerStyles: {},
          },
          ...blockEntries,
          ...placedCustomFields,
          // These two notices are UNMANAGED by applyStaticFieldOrder, and the
          // helper writes the configured order back into the slots the managed
          // entries already hold. They must therefore sit after EVERY managed
          // entry -- the custom rows placed in this section are managed too,
          // so anywhere before them still splits the run and the notice drifts
          // away from the input that raises it.
          {
            type: 'custom',
            name: 'duplicate_email_warning',
            Field: () => {
              if (!duplicateContact?.id) return null;
              return (
                <Flex mt="-8px" mb="12px" gap="4px" alignItems="center">
                  <Text size="xs" color="error-200">
                    Candidate already exists.
                  </Text>
                  <Link
                    href={`/candidates/${duplicateContact.id}`}
                    isExternal
                    color="error-200"
                    fontSize="xs"
                    fontWeight="semiBold"
                    textDecoration="underline"
                  >
                    View profile
                  </Link>
                </Flex>
              );
            },
          },
          {
            type: 'custom',
            name: 'delete_banner_for_contact',
            Field: () => {
              if (
                !isDeletedContact?.deleted ||
                !isDeletedContact?.deletedBannerForEmailMatch
              )
                return null;
              return (
                <UnDeleteCandidateBanner
                  reason={isDeletedContact?.deleted_reason}
                  deleted_by={isDeletedContact?.deleted_by}
                />
              );
            },
          },
        ],
        personalInfo,
        fieldNames,
        // Plain fields DO get the required marker; the four block wrappers never
        // may — a wrapper's own name reaches constructYupSchema against a key
        // formik does not hold, which makes the drawer unsubmittable.
        {
          neverStamp: BLOCK_ENTRY_NAMES,
          // Hidden rows the resume already filled still render — see above.
          revealHidden: revealHiddenIds,
          forceRequired: forceRequiredIds,
        }
      )
    )
  );

  // Custom fields the admin left in the Custom fields section render as their
  // own group after the stream, with the same header treatment the composite
  // blocks get. Deliberately NO `name`: like the layout wrappers above, a
  // wrapper's own key reaches constructYupSchema and formik holds no value for
  // it. Omitted entirely when the account has none, so the header never sits
  // over an empty group.
  if (!trailingCustomFields.length) return orderedStream;

  return [
    ...orderedStream,
    {
      type: 'wrapper',
      Wrapper: CollapseSection,
      wrapperProps: {
        title: CUSTOM_FIELDS_SECTION_TITLE,
        HeaderComp: PillSectionHeader,
        isHeaderClickable: false,
        defaultIsOpen: true,
        ...collapseSectionProps(),
      },
      fields: trailingCustomFields,
    },
  ];
};

type ResumeConfig = {
  generation_id?: string;
};

type ResumeDataType = {
  config?: ResumeConfig;
  resume?: {
    Education?: any;
    Experience?: any;
    LinkedInURL?: string;
    GithubURL?: string;
    Country?: string;
  };
};

export const useCreateContactConfig = ({
  parsedResume = {} as ResumeDataType,
  resumeValues = {},
  getDegree,
  formRef,
  defaults = [],
  setDeleted,
  isDeletedContact,
  setDuplicateContact,
  duplicateContact,
  hasLinkedInUrl = false,
}) => {
  const [formData, setFormData] = useState({
    profile: {},
    jobs: [],
    flows: [],
    projects: [],
    attributions: [],
    socials: [],
    education: [],
    experience: [],
  });

  const [resumeData, setResume] = useState<ResumeDataType>(parsedResume);
  const { jobId, projectId } = useParams();
  // Only inside a job: Fraud detector and Coordinator are configured per job,
  // and the standalone drawer has neither to read. Same two reads JobPosting
  // uses for the application form's Phone tooltip.
  const isFraudEnabled = useFraudDetectionJobEnabled(jobId, Boolean(jobId));
  const { data: coordinatorConfig } = useCoordinatorJobConfig(
    jobId,
    Boolean(jobId)
  );
  const isCoordinatorEnabled = Boolean(coordinatorConfig?.enabled);
  const { data: atsJobs = [], isLoading: isAtsJobsLoading } =
    useGetAllJobsListQuery({
      params: {
        scope: 'viewable',
        filter_by: { job_statuses: ['published'] },
      },
    });

  const { currentJob = {} } = useSelector(getAtsJobCreate);

  const { data: { name = '' } = {} } = currentJob;

  const [customForm, setCustomForm] = useState([]);

  const {
    // `form_fields` already returns ats_form_template.static_fields alongside
    // the custom fields, so the configured order costs no extra request.
    data: customFieldsData,
    isLoading: isCustomFieldsLoading,
  } = useCustomFormFields({ subject_type: 'contact' }, { staleTime: 0 });

  // Contacts moved to the row model, so `form_fields` returns the static rows
  // alongside the custom ones. This drawer renders the static fields from its
  // own config, so passing them through would render every default twice.
  // Memoised: this is the sole dependency of the effect below, which sets
  // state. `customFormFields` filters, so an unmemoised call would hand the
  // effect a new array on every render and spin it forever. (The job hook
  // wraps the same call for the same reason.)
  const ats_form_fields = useMemo(
    () => customFormFields(customFieldsData?.ats_form_fields),
    [customFieldsData?.ats_form_fields]
  );
  // Configured sections for this subject. Read once: addSection, the profile
  // section and the repeatable blocks all order themselves from it.
  const { feJobCandidateFieldReorder } = useKFlags();

  // Off, the drawer renders exactly what it did before the reorder work:
  // built-in order, nothing hidden, Attribution and the repeatable blocks as
  // their own trailing sections. Withholding the sections handles order and
  // visibility (applyStaticFieldOrder treats an absent section as "keep the
  // built-in order"); `blockEntries` below handles the composition.
  const staticFields = feJobCandidateFieldReorder
    ? customFieldsData?.ats_form_template?.static_fields
    : undefined;

  useEffect(() => {
    if (ats_form_fields?.length > 0) {
      const res = generateCustomFormConfig(
        {
          ats_custom_fields: ats_form_fields,
        },
        true
      );
      const custom = res?.custom ?? [];
      setCustomForm([...custom]);
    }
  }, [ats_form_fields]);

  useEffect(() => {
    setResume((prev) => {
      return prev?.config?.generation_id !==
        parsedResume?.config?.generation_id ||
        (prev?.config?.generation_id === undefined &&
          parsedResume?.config?.generation_id)
        ? parsedResume
        : prev;
    });
  }, [parsedResume]);

  const { Education, Experience, LinkedInURL, GithubURL, Country } =
    resumeData?.resume || {};

  useEffect(() => {
    if (jobId) {
      const jobOptions = { value: parseInt(jobId), label: name };
      defaults?.map((item) => addSection(item, [jobOptions]));
    } else {
      defaults?.map((item) => addSection(item, atsJobs));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Attribution builds its fields once and keeps them, so it has to wait for
  // the configured order to arrive — added on mount it would freeze the
  // built-in order while the query was still in flight. The repeatable blocks
  // don't need this: they re-derive their order on every render.
  // `addSection` ignores a second 'attributions' call, so this stays a no-op
  // if the effect re-runs.
  useEffect(() => {
    if (isCustomFieldsLoading) return;
    addSection('attributions');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCustomFieldsLoading]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      socials: [],
      education: [],
      experience: [],
    }));
    const addSections = () => {
      const urlRegex =
        /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;

      if (LinkedInURL && REGEX_PATTERNS.linkedin.test(LinkedInURL))
        addSection('socials');
      if (GithubURL?.length > 0 && urlRegex.test(GithubURL[0]))
        addSection('socials');
      if (Education?.length > 0)
        Education.forEach((i, ind) =>
          setTimeout(
            () => {
              addSection('education');
            },
            (ind + 1) * 500
          )
        );
      if (Experience?.length > 0)
        Experience.forEach((i, ind) =>
          setTimeout(
            () => {
              addSection('experience');
            },
            (ind + 1) * 500
          )
        );
    };

    addSections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeData]);

  const customFields = [...customForm];

  const users = useSelector(selectUsers);
  const currUser = useSelector(selectCurrUser);

  const hasResume = Boolean(parsedResume?.resume);
  const defaultPhoneCountry = hasResume
    ? undefined
    : getCountryFromTimezone(currUser?.time_zone) ||
      getCountryFromTimezone(currUser?.account?.time_zone) ||
      undefined;

  const fieldsConfig = {
    jobs: jobSectionConfig,
    flows: flowsSectionConfig,
    projects: projectsSectionConfig,
    attributions: attributionsSectionConfig,
    socials: socialsSectionConfig,
    education: educationSectionConfig,
    experience: experienceSectionConfig,
  };

  const addSection = (section, moreOptions = []) => {
    setFormData((prev) => {
      // Attribution is a singleton section (no "Add" button) — never append it
      // more than once, even if the init effect runs twice (e.g. StrictMode or
      // when it's also listed in `defaults`).
      if (section === 'attributions' && prev.attributions?.length > 0) {
        return prev;
      }

      const newSectionFields = fieldsConfig[section]?.({
        section,
        formData: prev,
        formRef,
        setFormData,
        users,
        staticFields,
        moreOptions,
        jobId,
        projectId,
        getDegree,
        setDeleted,
      });

      return {
        ...prev,
        [section]: [...prev[section], ...newSectionFields],
      };
    });
  };

  const generateSectionConfig = (
    section,
    sectionName,
    moreOptions = [],
    // Per-field rather than per-section: Source and Credited to each carry
    // their own status, so one can be Mandatory or Hidden while the other is
    // not. The repeatable blocks pass neither and behave as before.
    { requiredFieldNames = null, hiddenFieldNames = null, emptyText = '' } = {}
  ) => {
    // Each social link contains 4 fields.
    // The last social link does not require a bottom divider, so we subtract 1 from the total field count.
    const MAX_SOCIAL_LINKS_FIELDS = CONTACT_SOCIALS?.length * 4 - 1;

    const shouldShowActionButton = () => {
      if (section === 'socials') {
        return formData.socials.length < MAX_SOCIAL_LINKS_FIELDS;
      }
      return !['flows', 'projects', 'attributions', jobId && 'jobs'].includes(
        section
      );
    };

    // An empty section could always be added to; a non-empty one only where the
    // section takes more than one entry. Preserved exactly as the body button
    // behaved, so moving it into the header changes placement and nothing else.
    const isEmpty = (formData?.[section] || []).length === 0;
    const canAdd = isEmpty || shouldShowActionButton();

    const headerAction = canAdd
      ? {
          text: `Add ${sectionName}`,
          onClick: () => addSection(section, moreOptions),
        }
      : undefined;

    // The design puts a line of muted copy under an empty section rather than
    // leaving a header over nothing.
    // Deliberately NO `name`, matching the "+ Add" button that used to occupy
    // this slot: a named entry reaches constructYupSchema against a key formik
    // does not hold. Padded like the plain-field runs, which the blocks sit
    // between.
    const emptyStateField = {
      type: 'custom',
      Field: () => (
        <Box px="2em" pb="4px">
          {/* sm-regular, not sm-medium: same 14px, weight 450 rather than 550.
              Taken from the type scale so it stays in step with it. */}
          <KText size="sm-regular" color="secondary-500" textAlign="center">
            {emptyText}
          </KText>
        </Box>
      ),
    };

    return [
      {
        id: section,
        // Named so settings can order and hide the block. The name reaches
        // constructYupSchema, which validates a wrapper's own key as well as
        // its `fields` — so this entry must never carry `isRequired` (the
        // ordering call passes stampRequired: false).
        name: section,
        type: 'wrapper',
        Wrapper: CollapseSection,
        wrapperProps: {
          title: sectionName,
          HeaderComp: PillSectionHeader,
          headerAction,
          isHeaderClickable: false,
          defaultIsOpen: true,
          ...collapseSectionProps(),
          collapseStyles: {
            ...collapseSectionProps().collapseStyles,
            // Same rhythm as a fields section: 16px below the pill, and a
            // bottom 8 that the form grid's 16 completes into the 24px before
            // the next pill. The empty state used to sit flush against its own
            // pill at 0.
            //
            // The top padding is also load-bearing with entries present: an
            // entry's delete button is `position: absolute; right: 0` inside a
            // zero-height relative box, so without it the first one floats up
            // into the header's Add button.
            padding: '16px 2em 8px',
          },
        },
        fields: [
          ...(isEmpty
            ? [emptyStateField]
            : (formData?.[section] || [])
                // A hidden field is dropped outright.
                .filter(
                  (entry) => !(entry?.name && hiddenFieldNames?.has(entry.name))
                )
                .map((entry) =>
                  entry?.name && requiredFieldNames?.has(entry.name)
                    ? { ...entry, isRequired: true }
                    : entry
                )),
        ].filter(Boolean),
      },
    ];
  };

  // One default section now holds the plain fields, the four composite blocks
  // and any custom field dragged in among them.
  const defaultFields = getStaticSection(
    staticFields,
    PERSONAL_INFO_SECTION_ID
  );
  // Only the three repeatable blocks are gated this way: their formik key IS
  // the static field id. Anything else in the section is a custom field the
  // admin dragged in, whose row id is not a formData key — gating on it would
  // be permanently unsatisfiable, so it is left to its own field validation.
  const missingRequiredSections = defaultFields
    .filter((field) => ADDITIONAL_DETAILS_NAMES[field.id])
    .filter((field) => field.required && !field.hidden)
    .filter(
      (field) =>
        (formData?.[ADDITIONAL_DETAILS_NAMES[field.id]] || []).length === 0
    )
    .map((field) => field.label || field.id);

  // A Mandatory repeatable block means "at least one entry". Opening an empty
  // one makes that visible in the form instead of surfacing it as a toast on
  // submit, when the recruiter has already filled everything else.
  //
  // Tracked in a ref, not derived from the data: once opened, the recruiter may
  // legitimately delete the row again, and re-adding it on the next render
  // would make the block impossible to empty.
  const openedRequiredBlocks = useRef(new Set());
  useEffect(() => {
    if (!feJobCandidateFieldReorder) return;

    Object.entries(ADDITIONAL_DETAILS_NAMES).forEach(([fieldId, section]) => {
      const row = defaultFields.find((field) => field.id === fieldId);
      if (!row?.required || row?.hidden) return;
      if (openedRequiredBlocks.current.has(section)) return;
      if ((formData?.[section] || []).length > 0) return;

      openedRequiredBlocks.current.add(section);
      addSection(section);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feJobCandidateFieldReorder, defaultFields, formData]);

  const firstName = formRef?.current?.values?.first_name?.toLowerCase();
  const lastName = formRef?.current?.values?.last_name?.toLowerCase();

  const globalFormConfig = [
    ...profileSectionConfig({
      Country,
      customFields,
      firstName,
      lastName,
      // Drives the hidden-field reveal. Deliberately the RESUME-derived values,
      // not the live form: a job/flow default or the recruiter's own typing
      // must not resurrect a row the admin hid.
      resumeValues,
      isFraudEnabled,
      isCoordinatorEnabled,
      setDeleted,
      isDeletedContact,
      setDuplicateContact,
      duplicateContact,
      hasLinkedInUrl,
      defaultPhoneCountry,
      staticFields,
      // The four composite blocks are ordered in the SAME call as the plain
      // fields, so a block can sit anywhere among them and a field placed after
      // a block renders after it.
      // Only woven into the default stream when the flag is on. Off, they stay
      // where they always were: their own sections after Job / Flow / Project.
      blockEntries: feJobCandidateFieldReorder
        ? [
            // Source and Credited to join the stream as PLAIN fields, not as an
            // "Attribution" section: the design puts them inside Personal info
            // alongside First name. Being ordinary managed entries, they get
            // their position, their Hide and their Mandatory marker from
            // applyStaticFieldOrder like every other field -- which is why the
            // per-field required/hidden sets are no longer threaded through
            // generateSectionConfig.
            ...attributionsSectionConfig({ users }),
            ...generateSectionConfig('socials', 'Social Link', [], {
              emptyText: 'No social links added',
            }),
            ...generateSectionConfig('education', 'Education', [], {
              emptyText: 'No education added',
            }),
            ...generateSectionConfig('experience', 'Experience', [], {
              emptyText: 'No experience added',
            }),
          ]
        : [],
      // formRef,
    }),
    // Jobs / Flows / Projects are drawer-only sections: they are not candidate
    // fields, so settings never orders them and they stay pinned after the
    // configured stream.
    ...generateSectionConfig('jobs', 'Job', atsJobs, {
      emptyText: 'Not added to any job',
    }),
    ...(!jobId
      ? [
          ...generateSectionConfig('flows', 'Flow', [], {
            emptyText: 'Not added to any flow',
          }),
          ...generateSectionConfig('projects', 'Project', [], {
            emptyText: 'Not added to any project',
          }),
        ]
      : []),
    // Flag off: the pre-reorder layout, with these trailing in a fixed order.
    ...(feJobCandidateFieldReorder
      ? []
      : [
          ...generateSectionConfig('attributions', 'Attribution'),
          ...generateSectionConfig('socials', 'Social Link'),
          ...generateSectionConfig('education', 'Education'),
          ...generateSectionConfig('experience', 'Experience'),
        ]),
  ];

  return {
    formData,
    globalFormConfig,
    // Blocks marked Mandatory in settings that the user has not filled in.
    // "Mandatory" on a repeatable block means at least one entry, which no
    // per-field Yup rule can express, so the drawer checks these on save.
    missingRequiredSections,
    isLoading: isAtsJobsLoading || isCustomFieldsLoading,
  };
};
